import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading, PrimaryButton } from './ui/UI';
/**
 * First Report — the wedge offering.
 *
 * Previously: a fake "attack-path-report.pdf" mockup window with macOS-style
 * traffic-light dots, a fake "3 critical paths require remediation"
 * executive summary, a scanning animation across the panel, and 8 chip-tags.
 * That's the canonical fake-dashboard AI tell. Real buyers see through it.
 *
 * Now: a plain explanation of what's in a real report. No fake numbers, no
 * fake screenshot, no scanning effect. The 6-step flow and the 8 contents
 * stay — they're the substance — but presented as text, not as a synthetic
 * UI mockup pretending to be the product.
 */
const flow = ['Connect your environment', 'Run an attack-path report', 'See the few paths that matter', 'Fix the highest-impact risks', 'Verify closure', 'Export proof'];
const includes = ['Top realistic attack paths', 'Evidence behind each path', 'Plain-English explanation', 'Business impact', 'Remediation steps', 'Verification plan', 'Shareable summary', 'Technical appendix'];
export function FirstReport() {
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-435">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-436">
        <SectionLabel number="08" label="First Product" className="mb-8" data-id="element-437" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-438">
          {/* Headline + framing */}
          <div className="lg:col-span-7" data-id="element-439">
            <SectionHeading size="xl" className="mb-8" data-id="element-440">
              Get your first
              <br data-id="element-441" />
              <span className="text-white/40" data-id="element-442">attack-path report.</span>
            </SectionHeading>
            <p className="text-lg text-white/65 leading-relaxed mb-6 max-w-2xl" data-id="element-443">
              The first Periscan experience is simple. No 500-page pentest. No
              raw scanner dump. No guessing what to fix first.
            </p>
            <p className="text-lg text-white/65 leading-relaxed mb-12 max-w-2xl" data-id="element-444">
              Just the few paths that actually lead to business impact, the
              evidence behind each one, and the remediation that closes them.
            </p>

            <PrimaryButton href="/early-access" size="lg" data-id="element-445">
              Request Early Access
            </PrimaryButton>

            <p className="mt-6 text-sm text-white/45 max-w-md" data-id="element-446">
              Early-access reports may be guided during the first beta phase
              while the self-serve product is finalized.
            </p>
          </div>

          {/* Right column — typography, not fake UI */}
          <div className="lg:col-span-5 space-y-16" data-id="element-447">
            {/* The flow */}
            <div data-id="element-448">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-449">
                The Flow
              </p>
              <ol className="border-t border-white/5" data-id="element-450">
                {flow.map((f, i) => <motion.li key={f} initial={{
                opacity: 0,
                x: -6
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.35,
                delay: i * 0.04
              }} className="border-b border-white/5 py-3 flex items-baseline gap-4" data-id="element-451">
                    <span className="font-mono text-xs text-[#1a8fd4]/70 tracking-[0.2em] w-8" data-id="element-452">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-base text-white" data-id="element-453">{f}</span>
                  </motion.li>)}
              </ol>
            </div>

            {/* What's actually in it */}
            <div data-id="element-454">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-455">
                What's in the report
              </p>
              <ul className="space-y-2.5" data-id="element-456">
                {includes.map((item, i) => <motion.li key={item} initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: i * 0.03
              }} className="text-base text-white/75 flex items-baseline gap-3" data-id="element-457">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-458">
                      ·
                    </span>
                    {item}
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}